import { useState, type ReactNode } from 'react';
import { journalApi } from '../api/journal';
import type { DrillView } from '../components/analytics/drillView';

/**
 * Document numbers mentioned in free text become NAVIGATION: "Grąžinimas už
 * klaidingą mokėjimą BNK-2025-0005" — click BNK-2025-0005, land on that entry.
 * The accountant moves back and forth between items until there is enough
 * information to decide; the app never makes them re-type a number into search.
 *
 * Resolution is click-time (not render-time): journalApi.search is a CONTAINS
 * query over Content + DocumentNumber, so we exact-match on DocumentNumber
 * client-side and drill to the (first) hit.
 */

// Doc-number shapes the books actually use: BNK-2025-0005, KEITO/2024-11-14/001,
// SF-12, KOMP-2025-001, PARD1-11 — letters, then a year, then counters.
const DOC_NUMBER_RE = /\b[A-ZĄČĘĖĮŠŲŪŽ]{2,}[-/][0-9]{2,4}(?:[-/][0-9-]+)?\/?[0-9]\b/g;

export function linkifyDescription(
  text: string,
  onDrill: (view: DrillView) => void,
  onNotFound?: (token: string) => void,
): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of text.matchAll(DOC_NUMBER_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) nodes.push(text.slice(last, idx));
    nodes.push(
      <DocLink key={`dl${key++}`} token={m[0]} onDrill={onDrill} onNotFound={onNotFound} />,
    );
    last = idx + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function DocLink({ token, onDrill, onNotFound }: {
  token: string;
  onDrill: (view: DrillView) => void;
  onNotFound?: (token: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  const resolve = async () => {
    setBusy(true);
    try {
      const results = await journalApi.search(token, 50);
      const exact = results.filter(r => r.documentNumber === token);
      const hit = exact[0] ?? results.find(r => r.documentNumber?.includes(token));
      if (hit) {
        onDrill({ kind: 'journal', journalEntryId: hit.id });
      } else {
        onNotFound?.(token);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={resolve}
      disabled={busy}
      className="text-xs font-mono text-primary-700 hover:text-primary-900 underline decoration-primary-300 underline-offset-2 disabled:opacity-50 px-0.5"
      title={busy ? '…' : token}
    >
      {busy ? '…' : token}
    </button>
  );
}
